import {
    SearchUnteraufgabenQuery,
    useDeleteUnteraufgabe,
    useSearchUnteraufgaben
} from "../../client/hooks";
import {QueryClient, useMutation, useQuery, useQueryClient} from "react-query";
import {useTranslation} from "react-i18next";
import {Unteraufgabe, UnteraufgabenListe} from "../../api";
import {useNavigate} from "react-router-dom";
import {permissions} from "../../permissions";
import {useUserMandant} from "../useUserMandant";
import {PointerSensor, useSensor, useSensors} from "@dnd-kit/core";
import {arrayMove} from "@dnd-kit/sortable";

export const useUnteraufgabenHandling = () => {
    const {t: translation} = useTranslation('unteraufgaben');
    const  {t: translationLanguagesCodes} = useTranslation('sprachen');
    const queryClient: QueryClient = useQueryClient();
    const navigate = useNavigate();
    const {isSystemUser, checkMandantAndHasOneOfPermissions, checkHasOneOfPermissions} = useUserMandant();
    const sensors = useSensors(useSensor(PointerSensor));

    const deleteUnteraufgabe: (aufgabeId: string, unteraufgabeId: string) => Promise<Unteraufgabe> = useDeleteUnteraufgabe();
    const searchUnteraufgaben: (aufgabeId: string, query: SearchUnteraufgabenQuery) => Promise<UnteraufgabenListe> = useSearchUnteraufgaben();

    const deleteUnteraufgabeMutation = useMutation(
        ({aufgabeId, unteraufgabeId}: {
            aufgabeId: string;
            unteraufgabeId: string
        }): Promise<Unteraufgabe> => deleteUnteraufgabe(aufgabeId, unteraufgabeId),
        {
            onSuccess: async (_data, variables: { aufgabeId: string; unteraufgabeId: string }) => {
                const {aufgabeId, unteraufgabeId} = variables;
                await queryClient.invalidateQueries(["unteraufgaben"]);
                navigate(`/aufgaben/${aufgabeId}/unteraufgaben`);
            },
        }
    );

    const loadAllUnteraufgaben = async (aufgabeId: string, params: any): Promise<UnteraufgabenListe> => {
        const search: string | undefined = undefined;
        const filtered: string | undefined = params.filtered ?? undefined;
        const page: number = params.page ?? 0;
        const limit: number = params.limit ?? 25;
        const sort: string | undefined = params.sort ?? undefined;

        return await searchUnteraufgaben(
            aufgabeId, {
                search: search,
                filtered: filtered,
                page: page,
                limit: limit,
                sort: sort,
            })
    }

    const useUnteraufgabenListe = (aufgabeId: string) => {
        // suspense is disabled here because this query gets invalidated/refetched after
        // saving a Unteraufgabe (see useUnteraufgabenForm and deleteUnteraufgabeMutation
        // below). If that background refetch fails, a suspense query would throw during
        // render and crash to the global error boundary even though the save itself
        // succeeded. Errors are surfaced to the caller via `error` instead.
        const {data, isLoading, error} = useQuery(
            ['unteraufgaben', aufgabeId],
            () => loadAllUnteraufgaben(aufgabeId, {}),
            {suspense: false}
        );

        return {
            unteraufgaben: data?.items.sort((a: Unteraufgabe, b: Unteraufgabe) => a.reihenfolge - b.reihenfolge) ?? [],
            isLoading,
            error
        };
    };

    const canCreateUnteraufgabeField = () => {
        return (
            isSystemUser ||
            checkHasOneOfPermissions([
                    permissions.unteraufgaben.create,
                    permissions.unteraufgaben.own_create,
                ]
            ));
    }

    const canEditUnteraufgabeField = (mandantId: string) => {
        return (
            isSystemUser ||
            checkMandantAndHasOneOfPermissions(mandantId,
                [
                    permissions.unteraufgaben.edit,
                    permissions.unteraufgaben.own_edit,
                ]
            ));
    }

    const handleDragEnd = (
        event: any,
        items: Unteraufgabe[],
        formikSetFieldValue: (field: string, value: any) => void
    ) => {
        const {active, over} = event;

        if (active.id !== over?.id) {
            const oldIndex = items.findIndex(item => item.id === active.id);
            const newIndex = items.findIndex(item => item.id === over.id);

            formikSetFieldValue('unteraufgaben', arrayMove(items, oldIndex, newIndex));
        }
    };

    return {
        translationLanguagesCodes,
        translation,
        sensors,
        deleteUnteraufgabeMutation,
        loadAllUnteraufgaben,
        canCreateUnteraufgabeField,
        canEditUnteraufgabeField,
        useUnteraufgabenListe,
        handleDragEnd,
    }
}